"use client";

import { useState } from "react";
import type { PipelineCandidate } from "@/lib/api/types";
import { Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import { useRevealContact } from "@/hooks/use-contact-reveal";
import { ContactRevealConfirmAction } from "@/features/candidate/components/profile/contact/actions/reveal-confirm-action";
import { ContactReviewModal } from "@/features/candidate/components/profile/contact/actions/review-modal";
import {
  formatPhone,
  PhoneFlag,
} from "@/features/candidate/components/profile/contact/item";
import { isEnrichOrigin } from "@/components/ui/profile-origin";

export function EmailCell({ candidate }: { candidate: PipelineCandidate }) {
  const [revealOpen, setRevealOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const revealMutation = useRevealContact(candidate.profile_id);

  const handleRevealConfirm = async () => {
    await revealMutation.mutateAsync();
    setRevealOpen(false);
    setReviewOpen(true);
  };

  const revealed = candidate.already_revealed ?? false;
  const revealAction = isEnrichOrigin(candidate.origin) ? "enrich" : "reveal";
  // Real contacts only — drop legacy `marker` sentinel rows that can ride
  // along in the pipeline payload (type is email|phone), and synthetic
  // `{profile_id}@arbi.email` placeholders minted for ATS promotion (the
  // backend already omits these, this is a belt-and-suspenders guard).
  const contacts = (candidate.contacts ?? []).filter(
    (c) =>
      (c.type === "email" || c.type === "phone") && c.source !== "placeholder",
  );
  const emails = contacts.filter((c) => c.type === "email");
  const phones = contacts.filter((c) => c.type === "phone");
  // Prefer an email for the inline value; fall back to a phone so a
  // phone-only candidate shows the number instead of a misleading Reveal CTA.
  const primaryContact = emails[0] ?? phones[0];
  // Count every other contact (emails + phones) we aren't showing inline.
  const extraCount = contacts.length - 1;

  // "Has something worth revealing from" — LinkedIn is the primary signal
  // our reveal waterfall (PDL → Kickbox → RocketReach → NumVerify) can
  // actually act on, with github/twitter as secondary paths. If none of
  // these exist, Reveal is pointless and we fall back to manual Add+.
  const hasSocials =
    !!candidate.linkedin_url ||
    !!candidate.github_url ||
    !!candidate.twitter_url ||
    (candidate.social_profiles ?? []).some((sp) => !!sp.url);

  function renderButton() {
    // 1. Any existing contact wins — show it inline regardless of reveal
    //    status. Imported profiles arrive with `source="upload"` contacts
    //    but `already_revealed=false`; we want the user to see the value,
    //    not a misleading Reveal CTA. Prefer an email; for a phone-only
    //    candidate, show the phone number instead.
    if (primaryContact) {
      const isPhone = primaryContact.type === "phone";
      const phone = isPhone ? formatPhone(primaryContact.value) : null;
      return (
        <Button
          variant="linear-aqua"
          type="button"
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            setReviewOpen(true);
          }}
          className="truncate max-w-full"
        >
          {phone ? (
            <span className="flex min-w-0 items-center gap-1.5">
              {phone.country && (
                <PhoneFlag countryCode={phone.country} className="h-3 w-4" />
              )}
              <span className="truncate text-xs">{phone.formatted}</span>
            </span>
          ) : (
            <span className="truncate text-xs">{primaryContact.value}</span>
          )}
          {extraCount > 0 && (
            <span className="ml-1 text-xs shrink-0">(+{extraCount})</span>
          )}
        </Button>
      );
    }

    // 2. No email yet, but we have something the enrichment waterfall
    //    can work with — offer Reveal. Skip this once the profile has
    //    already been revealed (i.e. a previous Reveal returned nothing),
    //    so we don't ask the user to burn a credit a second time; they
    //    can refresh or add manually from the Review modal instead.
    if (hasSocials && !revealed) {
      return (
        <Button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setRevealOpen(true);
          }}
          variant="linear-contact"
          size="xs"
          className="rounded-full px-2"
        >
          <Icon
            name="IconEmail1Sparkle"
            size={12}
            className="text-contact shrink-0"
          />
          {revealAction === "enrich" ? "Enrich" : "Reveal"}
        </Button>
      );
    }

    // 3. Nothing to reveal from AND no contact yet — user can still
    //    contribute an address manually via the Review modal.
    return (
      <Button
        variant="linear-light"
        type="button"
        size="xs"
        className="rounded-full"
        onClick={(e) => {
          e.stopPropagation();
          setReviewOpen(true);
        }}
      >
        <Icon name="IconPlusSmall" size={16} className="size-4" />
        Add
      </Button>
    );
  }

  return (
    <>
      {renderButton()}

      <ContactRevealConfirmAction
        open={revealOpen}
        onOpenChange={setRevealOpen}
        onConfirm={handleRevealConfirm}
        loading={revealMutation.isPending}
        action={revealAction}
        candidate={{
          name: candidate.full_name ?? "",
          jobTitle: candidate.job_title,
          company: candidate.company,
          avatarUrl: candidate.avatar_url,
        }}
      />

      {reviewOpen && (
        <ContactReviewModal
          profileId={candidate.profile_id}
          open
          onOpenChange={setReviewOpen}
          revealAction={revealAction}
          candidate={{
            name: candidate.full_name ?? "",
            jobTitle: candidate.job_title,
            company: candidate.company,
            avatarUrl: candidate.avatar_url,
          }}
        />
      )}
    </>
  );
}
